import { useState } from 'preact/hooks';
import { Modal } from '@/ui/Modal.jsx';
import { cart, cartTotal, clearCart } from '@/store/cart';
import { dashboard } from '@/store/dashboard';
import { createOrder } from '@/api';
import { navigate } from '@/router';
import { notify } from '@/store/ui';
import { numberFormat } from '@/utils/format';
import css from './CheckoutModal.module.css';

export function CheckoutModal({ onClose }) {
  const addresses = dashboard.value?.addresses ?? [];
  const [paymentType, setPaymentType] = useState('');
  const [addressId, setAddressId] = useState(addresses[0]?.id ?? '');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!paymentType) {
      setError('Выберите способ оплаты');
      return;
    }
    setSubmitting(true);
    try {
      const items = cart.value.map((item) => ({
        product_id: item.externalId || item.productId,
        sku: item.sku,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
      }));
      const addr = addresses.find((a) => String(a.id) === String(addressId));
      await createOrder({
        items,
        comment,
        payment_type: paymentType,
        currency: 'RUB',
        delivery_address: addr?.address ?? '',
      });
      clearCart();
      onClose?.();
      notify('Заказ создан! Менеджер свяжется для подтверждения.');
      navigate('orders');
    } catch (err) {
      setError(err.message || 'Ошибка при создании заказа');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open title="Оформление заказа" onClose={onClose}>
      <div class={css.summary}>
        <h4>Товары в заказе:</h4>
        {cart.value.map((item, i) => (
          <div key={i} class={css.summaryItem}>
            <span>
              {item.name} × {item.quantity}
            </span>
            <span>{numberFormat(item.price * item.quantity)} ₽</span>
          </div>
        ))}
        <div class={css.summaryTotal}>
          <span>Итого:</span>
          <span>{numberFormat(cartTotal.value)} ₽</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} class={css.form}>
        <div class={css.section}>
          <h4>Способ оплаты:</h4>
          <Radio
            name="payment"
            checked={paymentType === 'bank'}
            onChange={() => setPaymentType('bank')}
            label="Безналичный расчёт"
          />
          <Radio
            name="payment"
            checked={paymentType === 'cash'}
            onChange={() => setPaymentType('cash')}
            label="Наличные (при получении)"
          />
        </div>

        <div class={css.section}>
          <strong>Адрес доставки</strong>
          {addresses.length > 0 ? (
            <div class={css.addresses}>
              {addresses.map((a) => (
                <Radio
                  key={a.id}
                  name="address"
                  checked={String(a.id) === String(addressId)}
                  onChange={() => setAddressId(a.id)}
                  label={a.address}
                />
              ))}
            </div>
          ) : (
            <p class="empty-state">Адреса не добавлены. Обратитесь к менеджеру.</p>
          )}
        </div>

        <div class={css.section}>
          <label htmlFor="checkout-comment">Комментарий к заказу</label>
          <textarea
            id="checkout-comment"
            rows={3}
            value={comment}
            onInput={(e) => setComment(e.target.value)}
            placeholder="Особые пожелания, время доставки и т.д."
          />
        </div>

        {error && (
          <div class={css.error} role="alert">
            {error}
          </div>
        )}

        <div class={css.actions}>
          <button type="button" class="btn btn-secondary" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" class="btn btn-primary" disabled={submitting}>
            {submitting ? 'Отправляем…' : 'Подтвердить заказ'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Radio({ name, checked, onChange, label }) {
  return (
    <label class={css.radio}>
      <input type="radio" name={name} checked={checked} onChange={onChange} />
      <span>{label}</span>
    </label>
  );
}
